import toastMessage from "../../toastMessage";
import formatSelectData from "../../formatSelectData";
import { getStorage } from "../../storage";
import language from "../../../language";
import { defineRole } from "../../handleAccessRoles";
import { ROLE_BLOOD_TEST } from "../../../constants/ROLES";
import api from "../../api";

const API_URL = process.env.REACT_APP_BBMS_BASE_API;

export const sanitizeBloodTestApiParams = (params = {}) => {
  const filters = { ...params };

  const toIdArray = (value) => {
    if (!Array.isArray(value)) return value;
    return value.map((el) =>
      typeof el === "object" ? el?.value || el?._id : el,
    );
  };

  if (filters.centers && !filters.center) {
    filters.center = toIdArray(filters.centers);
  }

  if (filters.centerSites && !filters.centerSite) {
    filters.centerSite = toIdArray(filters.centerSites);
  }

  filters.center = toIdArray(filters.center);
  filters.centerSite = toIdArray(filters.centerSite);

  delete filters.centers;
  delete filters.centerSites;

  if (Array.isArray(filters.center)) {
    filters.center = JSON.stringify(filters.center);
  }

  if (Array.isArray(filters.centerSite)) {
    filters.centerSite = JSON.stringify(filters.centerSite);
  }

  if (filters.q) {
    filters.q = String(filters.q).trim();
  }

  // Blood test endpoints use collection date range; avoid sending both createdAt and collectionDate filters.
  if (filters.startCollectionDate || filters.endCollectionDate) {
    delete filters.startDate;
    delete filters.endDate;
  }

  if (filters.q) {
    delete filters.startDate;
    delete filters.endDate;
    delete filters.startCollectionDate;
    delete filters.endCollectionDate;
  }

  if (filters.donation || filters.donor) {
    delete filters.startDate;
    delete filters.endDate;
    delete filters.startCollectionDate;
    delete filters.endCollectionDate;
  }

  return filters;
};

const fetchData = async (lng = "english", params) => {
  try {
    const user = await getStorage();

    const canAccess = defineRole({
      roles: user.accessRole,
      menu: ROLE_BLOOD_TEST,
      operation: "read",
    });

    if (!canAccess) return { data: [], count: 0 };

    let filters = sanitizeBloodTestApiParams(params);

    if (filters.startCollectionDate || filters.q) {
      delete filters.startDate;
      delete filters.endDate;
    }

    if (filters.endCollectionDate || filters.q) {
      delete filters.endDate;
    }

    if (filters.q) {
      delete filters.startDate;
      delete filters.endDate;
      delete filters.startCollectionDate;
      delete filters.endCollectionDate;
    }

    if (filters.donation || filters.donor) {
      delete filters.startDate;
      delete filters.endDate;
      delete filters.startCollectionDate;
      delete filters.endCollectionDate;
    }

    const options = {
      method: "GET",
      url: `${API_URL}/bloodtest`,
      params: filters,
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const queryRes = await api(options);

    let data = queryRes.data;

    if (params.type === "dropdown") {
      return formatSelectData(queryRes.data, "description", "_id");
    }

    return data;
  } catch (error) {
    toastMessage("error", error);
  }
};

export default fetchData;

export const getBloodTestDashboardCounts = async (
  lng = "english",
  params = {},
) => {
  try {
    const user = await getStorage();

    const canAccess = defineRole({
      roles: user.accessRole,
      menu: ROLE_BLOOD_TEST,
      operation: "read",
    });

    if (!canAccess) return {};

    const requestParams = sanitizeBloodTestApiParams(params);
    const baseOptions = {
      method: "GET",
      params: requestParams,
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    try {
      const { data } = await api({
        ...baseOptions,
        url: `${API_URL}/bloodtest/dashboard-counts`,
      });
      return data || {};
    } catch (primaryError) {
      const statusCode =
        primaryError?.response?.status || primaryError?.status || 0;

      if (statusCode !== 404) {
        throw primaryError;
      }

      const { data } = await api({
        ...baseOptions,
        url: `${API_URL}/bloodTest/dashboard-counts`,
      });
      return data || {};
    }
  } catch (error) {
    toastMessage("error", error);
  }
};

export const searchBloodTest = async (
  lng = "english",
  { page, limit, type, donorNumber },
) => {
  try {
    const user = await getStorage();

    const canAccess = defineRole({
      roles: user.accessRole,
      menu: ROLE_BLOOD_TEST,
      operation: "read",
    });

    if (!canAccess) return { data: [], count: 0 };

    const options = {
      method: "GET",
      url: `${API_URL}/bloodtest/search`,
      params: {
        page: page,
        limit: limit,
        donorNumber,
      },
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const queryRes = await api(options);

    let { data, count, totalRecords } = queryRes.data;

    if (type === "dropdown") {
      return formatSelectData(queryRes.data, "description", "_id");
    }

    return !page && !limit ? queryRes.data : { data, count, totalRecords };
  } catch (error) {
    toastMessage("error", error);
  }
};

export const deletebloodtest = async (lng = "english", id) => {
  try {
    const user = await getStorage();

    const options = {
      method: "DELETE",
      url: `${API_URL}/bloodtest`,
      data: {
        id,
      },
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    await api(options);

    toastMessage("success", language[lng].success_delete_test);
  } catch (error) {
    toastMessage("error", error);
  }
};

export const bloodTestStatistic = async (
  lng = "english",
  {
    page,
    limit,
    center,
    centerSite,
    serologyTested,
    bloodGroupingTested,
    type,
    startCollectionDate,
    endCollectionDate,
  },
) => {
  try {
    const user = await getStorage();

    const canAccess = defineRole({
      roles: user.accessRole,
      menu: ROLE_BLOOD_TEST,
      operation: "read",
    });

    if (!canAccess) return { data: [], count: 0 };
    const options = {
      method: "GET",
      url: `${API_URL}/bloodTest/bloodTestStatistics`,
      params: {
        page: page,
        limit: limit,
        center,
        centerSite,
        serologyTested,
        bloodGroupingTested,
        type,
        startCollectionDate,
        endCollectionDate,
      },
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const queryRes = await api(options);

    let data = queryRes.data;

    return data;
  } catch (error) {
    toastMessage("error", error);
  }
};

export const getBloodtestById = async (id) => {
  try {
    console.log("Blooad test ", id);
    const user = await getStorage();

    const options = {
      method: "GET",
      url: `${API_URL}/bloodtest?id=${id}`,
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };

    const data = await api(options);

    if (Array.isArray(data?.data)) return data?.data[0];

    return data;
  } catch (error) {
    throw error;
  }
};